import { useState, useEffect, useContext, useRef } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Chip, Sheet, CircularProgress,
    Card, CardContent, Modal, ModalDialog, ModalClose, DialogContent,
    FormControl, FormLabel, Input, Textarea, Select, Option
} from '@mui/joy';
import { LayoutContext } from '../context';
import {
    APIGetDocuments, APIDownloadDocument, APIUploadDocument, APIDeleteDocument,
    APIGetDocumentCategories, APICreateDocumentCategory, APIGrantDocumentAccess
} from '../api';
import { Folder, DownloadSimple, Lock, UploadSimple, Trash, UserPlus, Plus, FileText } from "@phosphor-icons/react";

function formatFileSize(bytes) {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

export default function DocumentsPage() {
    const { token, user } = useContext(LayoutContext);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(null);
    const isAdmin = user?.role === 'admin';

    // Upload modal
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadTitle, setUploadTitle] = useState('');
    const [uploadDesc, setUploadDesc] = useState('');
    const [uploadAccess, setUploadAccess] = useState('free');
    const [uploadCategoryId, setUploadCategoryId] = useState('');
    const [uploading, setUploading] = useState(false);
    const [docCategories, setDocCategories] = useState([]);
    const fileRef = useRef(null);

    // New category modal
    const [catModalOpen, setCatModalOpen] = useState(false);
    const [newCatName, setNewCatName] = useState('');

    // Grant access modal
    const [grantOpen, setGrantOpen] = useState(false);
    const [grantDocId, setGrantDocId] = useState(null);
    const [grantEmail, setGrantEmail] = useState('');

    useEffect(() => {
        fetchDocuments();
    }, []);

    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await APIGetDocuments(token);
            if (response.success) {
                setCategories(response.categories || []);
            }
        } catch (err) {
            console.error('Error loading documents:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (doc) => {
        if (!doc.can_access) return;
        setDownloading(doc.id);
        try {
            const response = await APIDownloadDocument(token, doc.id);
            const contentDisposition = response.headers['content-disposition'];
            let filename = doc.title;
            if (contentDisposition) {
                const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                if (match) filename = decodeURIComponent(match[1].replace(/['"]/g, ''));
            }
            // Ensure filename has proper extension
            if (!filename.match(/\.\w{2,5}$/)) {
                const contentType = response.headers['content-type'] || '';
                const extMap = { 'application/pdf': '.pdf', 'application/zip': '.zip', 'image/png': '.png', 'image/jpeg': '.jpg' };
                filename += extMap[contentType] || '.pdf';
            }
            const contentType = response.headers['content-type'] || 'application/octet-stream';
            const url = window.URL.createObjectURL(new Blob([response.data], { type: contentType }));
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
            // Refresh to update download count
            fetchDocuments();
        } catch (err) {
            console.error('Download error:', err);
            alert('Ошибка при скачивании');
        } finally {
            setDownloading(null);
        }
    };

    const handleDelete = async (docId) => {
        if (!window.confirm('Удалить документ?')) return;
        try {
            const response = await APIDeleteDocument(token, docId);
            if (response.success) fetchDocuments();
        } catch (err) {
            alert('Ошибка при удалении');
        }
    };

    const openUploadModal = async () => {
        setUploadFile(null);
        setUploadTitle('');
        setUploadDesc('');
        setUploadAccess('free');
        setUploadCategoryId('');
        try {
            const response = await APIGetDocumentCategories(token);
            if (response.success) setDocCategories(response.categories || []);
        } catch {}
        setUploadOpen(true);
    };

    const handleUpload = async () => {
        if (!uploadFile) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('title', uploadTitle || uploadFile.name);
            formData.append('description', uploadDesc);
            formData.append('access_type', uploadAccess);
            if (uploadCategoryId) formData.append('category_id', uploadCategoryId);
            const response = await APIUploadDocument(token, formData);
            if (response.success) {
                setUploadOpen(false);
                fetchDocuments();
            }
        } catch (err) {
            alert('Ошибка при загрузке');
        } finally {
            setUploading(false);
        }
    };

    const handleCreateCategory = async () => {
        if (!newCatName.trim()) return;
        try {
            const response = await APICreateDocumentCategory(token, newCatName.trim());
            if (response.success) {
                setNewCatName('');
                setCatModalOpen(false);
                const catResponse = await APIGetDocumentCategories(token);
                if (catResponse.success) setDocCategories(catResponse.categories || []);
            }
        } catch {}
    };

    const handleGrantAccess = async () => {
        if (!grantEmail.trim() || !grantDocId) return;
        try {
            const response = await APIGrantDocumentAccess(token, grantDocId, grantEmail.trim());
            if (response.success) {
                alert('Доступ выдан');
                setGrantOpen(false);
                setGrantEmail('');
            } else {
                alert(response.error || 'Ошибка');
            }
        } catch {
            alert('Ошибка');
        }
    };

    const getAccessBadge = (doc) => {
        if (doc.access_type === 'free') return <Chip size="sm" variant="soft" color="success">Бесплатно</Chip>;
        if (doc.access_type === 'paid') return <Chip size="sm" variant="soft" color="warning">Premium</Chip>;
        return <Chip size="sm" variant="soft" color="neutral">По запросу</Chip>;
    };

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography level="h2">Документы</Typography>
                {isAdmin && (
                    <Button
                        startDecorator={<UploadSimple size={18} />}
                        onClick={openUploadModal}
                        sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                            color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                            '&:hover': {
                                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E0E0E2' : '#333333',
                            },
                        }}
                    >
                        Загрузить
                    </Button>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : categories.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <FileText size={48} style={{ color: 'var(--joy-palette-text-tertiary)', marginBottom: 8 }} />
                    <Typography level="body-lg" color="neutral">
                        Документы пока не добавлены.
                    </Typography>
                </Box>
            ) : (
                <Stack spacing={3}>
                    {categories.map((cat) => (
                        <Box key={cat.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, p: 1, bgcolor: 'background.level1', borderRadius: 'sm' }}>
                                <Folder size={20} />
                                <Typography level="title-md">{String(cat.name)}</Typography>
                                <Chip size="sm" variant="soft" color="primary">{cat.documents.length}</Chip>
                            </Box>

                            <Stack spacing={1} sx={{ pl: 1 }}>
                                {cat.documents.map((doc) => (
                                    <Card key={doc.id} variant="outlined" sx={{ borderRadius: 'md' }}>
                                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                                                        <FileText size={18} style={{ color: 'var(--joy-palette-text-secondary)' }} />
                                                        <Typography level="title-sm" noWrap>{String(doc.title)}</Typography>
                                                        {getAccessBadge(doc)}
                                                    </Stack>
                                                    {doc.description && (
                                                        <Typography level="body-xs" color="neutral" sx={{ ml: 3.5 }}>
                                                            {String(doc.description)}
                                                        </Typography>
                                                    )}
                                                    <Stack direction="row" spacing={1} sx={{ ml: 3.5, mt: 0.5 }}>
                                                        <Typography level="body-xs" color="neutral">
                                                            {formatFileSize(doc.file_size)}
                                                        </Typography>
                                                        {isAdmin && doc.download_count !== undefined && (
                                                            <Typography level="body-xs" color="neutral">
                                                                {doc.download_count} скач.
                                                            </Typography>
                                                        )}
                                                    </Stack>
                                                </Box>
                                                <Stack direction="row" spacing={0.5}>
                                                    {doc.can_access ? (
                                                        <Button
                                                            size="sm"
                                                            variant="solid"
                                                            startDecorator={<DownloadSimple size={16} />}
                                                            loading={downloading === doc.id}
                                                            onClick={() => handleDownload(doc)}
                                                            sx={{
                                                                bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                                                color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                                                '&:hover': {
                                                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E0E0E2' : '#333333',
                                                                },
                                                            }}
                                                        >
                                                            Скачать
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            size="sm"
                                                            variant="soft"
                                                            color="neutral"
                                                            startDecorator={<Lock size={16} />}
                                                            disabled
                                                        >
                                                            Нет доступа
                                                        </Button>
                                                    )}
                                                    {isAdmin && doc.access_type === 'restricted' && (
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            color="primary"
                                                            onClick={() => { setGrantDocId(doc.id); setGrantEmail(''); setGrantOpen(true); }}
                                                        >
                                                            <UserPlus size={16} />
                                                        </IconButton>
                                                    )}
                                                    {isAdmin && (
                                                        <IconButton
                                                            size="sm"
                                                            variant="outlined"
                                                            color="danger"
                                                            onClick={() => handleDelete(doc.id)}
                                                        >
                                                            <Trash size={16} />
                                                        </IconButton>
                                                    )}
                                                </Stack>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}

            {/* Upload Modal */}
            <Modal open={uploadOpen} onClose={() => setUploadOpen(false)}>
                <ModalDialog sx={{ maxWidth: 500, width: '90%' }}>
                    <ModalClose />
                    <Typography level="h4" sx={{ mb: 2 }}>Загрузить документ</Typography>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                startDecorator={<UploadSimple size={18} />}
                                onClick={() => fileRef.current?.click()}
                            >
                                {uploadFile ? uploadFile.name : 'Выбрать файл'}
                            </Button>
                            <input
                                type="file"
                                ref={fileRef}
                                hidden
                                onChange={(e) => {
                                    const f = e.target.files[0];
                                    if (f) {
                                        setUploadFile(f);
                                        if (!uploadTitle) setUploadTitle(f.name.replace(/\.[^.]+$/, ''));
                                    }
                                }}
                            />
                            <FormControl>
                                <FormLabel>Название</FormLabel>
                                <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Описание</FormLabel>
                                <Textarea minRows={2} value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)} />
                            </FormControl>
                            <FormControl>
                                <FormLabel>Доступ</FormLabel>
                                <Select value={uploadAccess} onChange={(e, v) => setUploadAccess(v)}>
                                    <Option value="free">Бесплатный (все юзеры)</Option>
                                    <Option value="paid">Платный (plan1)</Option>
                                    <Option value="restricted">Ограниченный (вручную)</Option>
                                </Select>
                            </FormControl>
                            <FormControl>
                                <FormLabel>
                                    Категория
                                    <IconButton size="sm" variant="plain" sx={{ ml: 0.5 }} onClick={() => { setNewCatName(''); setCatModalOpen(true); }}>
                                        <Plus size={16} />
                                    </IconButton>
                                </FormLabel>
                                <Select
                                    value={uploadCategoryId}
                                    onChange={(e, v) => setUploadCategoryId(v)}
                                    placeholder="Без категории"
                                >
                                    <Option value="">Без категории</Option>
                                    {docCategories.map(c => (
                                        <Option key={c.id} value={String(c.id)}>{c.name}</Option>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                loading={uploading}
                                disabled={!uploadFile}
                                onClick={handleUpload}
                                sx={{
                                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#F5F5F7' : '#191919',
                                    color: (theme) => theme.palette.mode === 'dark' ? '#191919' : '#FFFFFF',
                                    '&:hover': {
                                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#E0E0E2' : '#333333',
                                    },
                                }}
                            >
                                Загрузить
                            </Button>
                        </Stack>
                    </DialogContent>
                </ModalDialog>
            </Modal>

            {/* New Category Modal */}
            <Modal open={catModalOpen} onClose={() => setCatModalOpen(false)}>
                <ModalDialog sx={{ maxWidth: 350 }}>
                    <ModalClose />
                    <Typography level="title-lg" sx={{ mb: 1 }}>Новая категория</Typography>
                    <Stack spacing={1.5}>
                        <Input
                            placeholder="Название"
                            value={newCatName}
                            onChange={(e) => setNewCatName(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateCategory()}
                        />
                        <Button onClick={handleCreateCategory} disabled={!newCatName.trim()}>Создать</Button>
                    </Stack>
                </ModalDialog>
            </Modal>

            {/* Grant Access Modal */}
            <Modal open={grantOpen} onClose={() => setGrantOpen(false)}>
                <ModalDialog sx={{ maxWidth: 400 }}>
                    <ModalClose />
                    <Typography level="title-lg" sx={{ mb: 1 }}>Выдать доступ</Typography>
                    <Stack spacing={1.5}>
                        <Input
                            placeholder="Email пользователя"
                            value={grantEmail}
                            onChange={(e) => setGrantEmail(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleGrantAccess()}
                        />
                        <Button onClick={handleGrantAccess} disabled={!grantEmail.trim()}>Выдать</Button>
                    </Stack>
                </ModalDialog>
            </Modal>
        </Box>
    );
}
